/**
 * Campaign — one event's paid promotion as the console holds it. Pure state,
 * zero I/O:
 * every gesture on the surfaces in this folder is an intent on a Subject, and
 * `web/src/events/console/campaign.sync.ts` is the sole writer of
 * every cell below.
 *
 * The subject resolves itself. `consoleEventId` names the event whose console
 * is mounted and clears when it disconnects, so the destination takes no
 * property from the rail that routes it.
 *
 * ── ONE CAMPAIGN ON SCREEN, MANY IN THE PATH ───────────────────────────────
 * A campaign is stored under the event it promotes, so an event may carry
 * several over its life. The destination renders one: whichever the operator
 * can still act on, and — when every one of them is over — the most recent.
 * There is no created-at on the document, so "most recent" is read off the
 * stamps the lifecycle itself writes.
 *
 * ── THE UNIT IS READ, NEVER ASSUMED ────────────────────────────────────────
 * Every money figure here is an integer in the ad account's own minor unit,
 * and the ad account is free to bill in a currency this shop's ticket prices
 * are not in. `campaignCurrency` is the one place that answers which, and it
 * answers `null` until something has told it — a surface that would have to
 * name an amount before then says so instead of picking a symbol.
 */

import { expandPermission } from "@mhmo91/ican";
import { computed, state } from "@mhmo91/schmancy/state";
import type { Locale } from "@momo/i18n";
import type {
	Campaign,
	CreativeAsset,
	ProposeCampaign,
	StopCampaign,
} from "@momo/types";
import { TicketType } from "@momo/types";
import { Subject } from "rxjs";
import { activeActor } from "../../../auth/actor.state.ts";
import { locationsState } from "../../../storefront/locations.state.ts";
import { ticketTypesState } from "../../ticket-types/ticket-types.state.ts";
import {
	consoleEvent,
	consoleEventId,
	consoleViewModel,
} from "../console.state.ts";

const DAY_MS = 86_400_000;

/**
 * How long a parked proposal stands before this surface says the world may
 * have moved under it. The confirmation is what actually refuses — it
 * re-reads the price floor and the stamp inside its own transaction — so this
 * bound governs a warning and never a gate, and a proposal past it is still
 * releasable.
 */
export const PROPOSAL_STALE_AFTER_MS = 7 * DAY_MS;

/** The stored body rejoined to the doc id it was read at. */
export type CampaignRow = Campaign.Campaign & { readonly id: string };

/** Workspace-wide campaign row used by the overview beside the event-scoped
 * console list. */
export type WorkspaceCampaignRow = CampaignRow & { readonly eventId: string };

/**
 * Same, for one campaign's own pictures, plus the display URL its
 * `storagePath` resolves to — no entity counterpart, since resolving it is
 * genuine I/O this pure state file cannot perform, exactly as `EventWithId`
 * carries `posterUrl` beside `poster`. Null when the resolve failed.
 */
export type CreativeAssetRow = CreativeAsset.CreativeAsset & {
	readonly id: string;
	readonly url: string | null;
};

// ── Live state singletons ──────────────────────────────────────────────────

export const consoleCampaigns = state<readonly CampaignRow[]>(
	"events/console-campaigns",
).memory([]);

export const workspaceCampaigns = state<readonly WorkspaceCampaignRow[]>(
	"workspace/campaigns",
).memory([]);

export const consoleCampaignsStatus = state<
	"loading" | "ready" | "denied" | "error"
>("events/console-campaigns-status").memory("loading");

export const campaignAssets = state<readonly CreativeAssetRow[]>(
	"events/campaign-assets",
).memory([]);

/** The bound every campaign in this workspace is measured against, from its
 *  own settings singleton. Null means no bound is set, which is the state the
 *  propose call refuses in; undefined means the read has not answered. */
export const adSpendCeilingMinor = state<number | null | undefined>(
	"events/ad-spend-ceiling",
).memory(undefined);

/** The unit the ad account bills in, from the workspace's own settings
 *  singleton. Null until an ad account has been stored — a legible state
 *  rather than an error, and the one every money question on these surfaces
 *  withholds itself on rather than guessing a symbol. */
export const adAccountCurrency = state<string | null | undefined>(
	"events/ad-account-currency",
).memory(undefined);

/**
 * Which language's ad is on screen. Ephemeral and per-visit — a way of
 * looking, not a fact about the campaign, so it never reaches the stored
 * proposal. A module cell rather than one component's own field because two
 * surfaces read it: the copy section switches it and the picture frame
 * renders under it, and an ad plus the words it carries is one thing seen
 * from two places.
 *
 * `null` means "whatever the operator's own interface is set to" — the switch
 * says the other language exists, it does not pick the first one.
 */
export const adLocale = state<Locale | null>(
	"events/campaign-ad-locale",
).memory(null);

/**
 * The composition's own refusal, as the call answered it — derived from the
 * contract's output union rather than restated, so a refusal the door learns
 * to give cannot go missing from the surface that has to render its remedy.
 * Each member is fixed somewhere different, so the offer switches per member
 * and there is no generic refused face to draw.
 */
export type CampaignProposeRefusal = Exclude<
	ProposeCampaign.ProposeCampaignOutput,
	{ outcome: "proposed" }
>;

export const campaignProposeRefusal = state<CampaignProposeRefusal | null>(
	"events/campaign-propose-refusal",
).memory(null);

/** Whether a call that changes this campaign is in flight — a real round trip
 *  to the ad network's own door, so the press it came from says so rather
 *  than looking inert until the document changes underneath it. */
export const campaignActionNotice = state<string | null>(
	"events/campaign-action-notice",
).memory(null);

export const campaignPending = state<boolean>("events/campaign-pending").memory(
	false,
);

/**
 * Whether the signed-in actor may run paid promotion — mirrors
 * `campaign.entity.ts`'s one predicate (`tickets.ads.manage`). The mfa half
 * that predicate also carries is already true of anyone inside the admin
 * shell, so restating it would gate on a fact the shell has proved.
 */
export const canManageAds = computed(
	() => activeActor.get()?.permissions.includes("tickets.ads.manage") ?? false,
);

// expandPermission rather than a plain .includes, so a wildcard grant
// (`tickets.events.*`, `*`) still reaches the same door the server opens for
// it.
const eventsManageTokens = expandPermission("tickets.events.manage");
const credentialsManageTokens = expandPermission(
	"workspace.credentials.manage",
);

/**
 * Whether this operator may set the ad account up themselves, rather than be
 * told who can. Both halves are load-bearing and the platform keeps them
 * deliberately apart: `settings/private` — the document the credentials form
 * seeds from — is read under `tickets.events.manage`, while the save is
 * authorized against `workspace.credentials.manage`. An actor holding one
 * without the other would be handed a form that either opens empty or is
 * refused the moment they press Save.
 */
export const canConfigureAdAccount = computed(() => {
	const granted = activeActor.get()?.permissions ?? [];
	return (
		granted.some((permission) => eventsManageTokens.includes(permission)) &&
		granted.some((permission) => credentialsManageTokens.includes(permission))
	);
});

/**
 * How far the file the operator just handed over has got.
 *
 * `ready`      the intake square is a drop target and nothing else. A
 *              `fileName` still standing on a ready cell is the file that just
 *              arrived, kept for one thing: the live region's settled
 *              sentence, so the wait it announced has an audible end rather
 *              than going quiet
 * `sending`    bytes are moving, and `uploadBytesResumable` says how far
 * `preparing`  the bytes have landed; the document has not. Nothing measures
 *              this hop — it ends when the live asset query carries the
 *              trigger's own write back
 * `slow`       the wait outlived its bound with nothing having answered it.
 *              `asset.webhook.ts` fires once on an event the platform never
 *              re-delivers, so a lost fire is silence until
 *              `describe.schedule.ts` sweeps — five minutes away, which is the
 *              one thing a browser cannot wait out and the whole reason this
 *              face exists. It is also where a refusal this square cannot name
 *              truthfully lands: the weakest sentence that is still true
 * `refused`    these bytes did not become artwork, and another file is the way
 *              past it. TWO doors into one face — the device's own decoder
 *              refused before a byte left, or the server's reader refused
 *              after they landed (`creativeRefusals/{assetId}`, reason
 *              `unreadableBytes`) — because the operator's next move is the
 *              same move from both, and re-sending identical bytes to a
 *              deterministic reader is not it
 */
export type UploadPhase =
	| "ready"
	| "sending"
	| "preparing"
	| "slow"
	| "refused";

/**
 * One cell, because the intake is one cell: the operator's own square is both
 * the door and the whole readout for the file it took, so a second file cannot
 * be offered while the first is in flight — at that moment the square is not a
 * target. The same guarantee `exhaustMap` makes in the reconciler, stated in
 * the shape rather than only in the operator.
 *
 * Nothing here mirrors `CreativeAsset`: that entity describes bytes that have
 * arrived and been measured by the server, and every field below is about
 * bytes that have not.
 */
export type UploadCell = {
	readonly phase: UploadPhase;
	/** The operator's own name for the file — the one thing about it only their
	 *  device can see, and the caption the landed tile will carry. */
	readonly fileName: string;
	/** `URL.createObjectURL(file)` — the picture shown back to them while it
	 *  travels, so the square is holding the file rather than describing it.
	 *  Null only before anything has been offered: a settled cell keeps the
	 *  handle it stopped rendering so the next attempt is the one place that
	 *  frees it, which is what keeps one blob alive at a time rather than one
	 *  per file the operator ever hands over. */
	readonly url: string | null;
	/** How much of the file has gone, 0–1. Meaningless outside `sending`. */
	readonly progress: number;
};

export const UPLOAD_READY: UploadCell = {
	phase: "ready",
	fileName: "",
	url: null,
	progress: 0,
};

export const uploadCell = state<UploadCell>("events/campaign-upload").memory(
	UPLOAD_READY,
);

/**
 * How long the square waits before it says so.
 *
 * Here rather than inside a reconciler because every reconciler that answers
 * this cell owes the same bound — the storefront's, which waits on Firestore,
 * and the studio's, which waits on a timer standing where Firestore does — and
 * a bound they spelled separately would let a proof settle on a different
 * surface than the one that ships.
 *
 * Twelve seconds is the trigger's own worst ordinary case with room over it: a
 * cold start plus a download of up to `CREATIVE_MAX_BYTES` plus a header read.
 * What it is NOT sized for is the lost fire, which is silence until the sweep
 * — nothing bounds that, which is why the face this bound produces says the
 * picture has not arrived rather than that it failed.
 */
export const UPLOAD_SLOW_AFTER_MS = 12_000;

// ── Intents ────────────────────────────────────────────────────────────────

/**
 * "Compose an ad for this amount." One channel for the offer's first press
 * and for a later re-draft alike: the campaign's own stored status is what
 * separates the two, so a caller-set mode could only ask for a composition
 * that state refuses. A null id mints the draft first.
 */
export const campaignProposeRequested$ = new Subject<{
	readonly eventId: string;
	readonly campaignId: string | null;
	readonly budgetMinor: number;
}>();

/**
 * "Set the ad account up from here." Raised where the missing configuration
 * is actually felt rather than named as somewhere else to go, and answered by
 * whichever app mounts this destination — the storefront opens the Settings
 * credentials form over the console. Carries nothing: the form reads the one
 * shop's own settings document and there is no second ad account to name.
 */
export const adAccountConfigureRequested$ = new Subject<void>();

/**
 * An edit to the parked proposal — the picture, the words, the two audience
 * dials, or the amount. Partial by design: the surface names exactly what the
 * operator just changed and the reconciler folds it into the stored map.
 *
 * `copy` names the language the edit is in, because the edit lands on that
 * locale's own entry in the additive per-locale map and nowhere else. The
 * composed source string is left alone: which locale IS the source is a fact
 * about the deployment that no document this client can read carries, so
 * moving it would be a guess written into the record.
 */
export const campaignProposalEdited$ = new Subject<{
	readonly eventId: string;
	readonly campaignId: string;
	readonly copy?: {
		readonly locale: Locale;
		readonly headline?: string;
		readonly body?: string;
	};
	readonly assetId?: string;
	readonly radiusKm?: number;
	readonly minAge?: number;
	readonly budgetMinor?: number;
}>();

/** The one gesture that commits money, fired only after the operator has
 *  answered the confirmation. */
export const campaignLaunchRequested$ = new Subject<{
	readonly eventId: string;
	readonly campaignId: string;
}>();

/** Pause, resume, or end the run — the door that makes starting a spend
 *  reversible from inside this product. */
export const campaignStopRequested$ = new Subject<{
	readonly eventId: string;
	readonly campaignId: string;
	readonly intent: StopCampaign.StopIntent;
}>();

/**
 * Artwork the operator brought, as the browser's own handle. One channel for
 * all three gestures the dropzone owns — drag, paste, picker — because they
 * are one intake, and naming or measuring the file is the reconciler's job and
 * never the surface's.
 */
export const artworkOffered$ = new Subject<{
	readonly eventId: string;
	readonly campaignId: string;
	readonly file: File;
}>();

/** "Send it again", from the bounded wait. The reconciler still holds the
 *  offer, so the retry costs the operator no second trip to their own
 *  folders. */
export const uploadRetried$ = new Subject<void>();

// ── Derivations ────────────────────────────────────────────────────────────

/**
 * The campaign this event's destination renders. A campaign the operator can
 * still act on outranks one whose run is over; among finished ones the most
 * recently stopped wins, since nothing else on the document orders them.
 */
export const openCampaign = computed<CampaignRow | null>(() => {
	const rows = consoleCampaigns.value;
	const stamp = (row: CampaignRow): number =>
		(row.stoppedAt ?? row.launchedAt ?? row.proposedAt)?.toMillis() ?? 0;
	return (
		rows.find((row) => row.status !== "ended") ??
		[...rows].sort((a, b) => stamp(b) - stamp(a))[0] ??
		null
	);
});

/**
 * The unit every figure on these surfaces counts in. A campaign that has been
 * composed carries the unit its own budget was read under, and that answer
 * outranks the ad-account reading for exactly that campaign; before there is
 * a campaign, the reconciler's own reading of the ad account is all there is.
 */
export const campaignCurrency = computed<string | null>(
	() => openCampaign.get()?.currency ?? adAccountCurrency.value ?? null,
);

/**
 * The cheapest a single ticket to this event can be bought for right now, in
 * euro cents — the figure the composed copy splices as literal text, and the
 * one a parked proposal is measured against.
 *
 * `@momo/types`' own derivation, never a second reading of it here. This
 * number is compared against the one the composition wrote onto the campaign
 * document, so a client funnel free to disagree with the server's is not a
 * smaller bug than a wrong price — it is a staleness banner firing on a
 * proposal composed one second earlier, and on every proposal after it.
 *
 * `null` where nothing is advertisable — an event no honest ad exists for,
 * which is the answer the composition refuses on rather than a floor.
 */
const advertisableFloorCents = computed<number | null>(() => {
	const event = consoleEvent.get();
	if (event === null) return null;
	return TicketType.resolvePriceFloor(
		ticketTypesState.value,
		event,
		Date.now(),
	);
});

/**
 * What the offer reads. Every number on it is this event's own: how much of
 * the quota has gone, how long is left to sell, and what a ticket on this
 * event has ACHIEVED on average so far — the rate that turns an amount of
 * money into a count of tickets honestly. Before anything has sold there is
 * no achieved rate, so it falls back to the cheapest ticket anyone can buy,
 * which is the most conservative reading available and cannot flatter the
 * offer.
 */
export const offerViewModel = computed<{
	readonly sold: number;
	readonly quota: number;
	readonly days: number;
	readonly purchasable: boolean;
	readonly ceilingMinor: number | null;
	readonly currency: string | null;
	/** Whether the ad account's bound and unit have both answered; until
	 *  then nothing about them is stated, configured or not. */
	readonly settingsAnswered: boolean;
	/** In euro cents, the unit ticket prices are stated in — never the ad
	 *  account's, which is free to be another. */
	readonly ticketCents: number;
}>(() => {
	const view = consoleViewModel.get();
	const event = consoleEvent.get();
	const now = Date.now();
	// Where the run stops, and deliberately not the advertisable floor above:
	// a tier that opens next week quotes no price today, and the campaign is
	// still running when it goes on sale, so its close bounds the run.
	const lastClose =
		event === null
			? now
			: (TicketType.resolvePublicTicketSalesEnd(
					ticketTypesState.value,
					event,
				) ?? now);
	const achieved =
		view !== null && view.totalSold > 0
			? view.grossTakenCents / view.totalSold
			: null;
	const floor = advertisableFloorCents.get();
	return {
		sold: view?.totalSold ?? 0,
		quota: view?.totalCapacity ?? 0,
		days: Math.max(1, Math.ceil((lastClose - now) / DAY_MS)),
		// The composition's own refusal condition, read the same way it reads
		// it: no floor is no ad, never a count of rows this surface filtered.
		purchasable: floor !== null,
		ceilingMinor: adSpendCeilingMinor.value ?? null,
		currency: campaignCurrency.get(),
		settingsAnswered:
			adSpendCeilingMinor.value !== undefined &&
			(openCampaign.get()?.currency != null ||
				adAccountCurrency.value !== undefined),
		ticketCents: Math.max(1, Math.round(achieved ?? floor ?? 1)),
	};
});

/**
 * What the proposal surface reads. Deliberately locale-free: which language's
 * pane is on screen is a way of looking that lives on the surface, and
 * folding it in here would make one component's ephemeral view state a
 * property of the campaign.
 */
export const proposalViewModel = computed<{
	readonly eventId: string;
	readonly campaign: CampaignRow;
	readonly proposal: Campaign.CampaignProposal;
	readonly assets: readonly CreativeAssetRow[];
	readonly asset: CreativeAssetRow | null;
	/** Whether the provenance label paints on this picture. Keyed off the
	 *  chosen picture's own provenance, never off the campaign: switching
	 *  between the operator's own artwork and a generated one adds or removes
	 *  the label in that same gesture. */
	readonly aiLabel: boolean;
	readonly days: number;
	readonly budgetMinor: number;
	readonly spentMinor: number;
	readonly currency: string | null;
	/** The venue the radius is measured around, in the operator's own words. */
	readonly place: string;
	/** Whether the world may have moved under a parked proposal. The precise
	 *  test is the one number the ad states; the age bound beside it catches
	 *  what the floor cannot see. */
	readonly stale: boolean;
} | null>(() => {
	const campaign = openCampaign.get();
	const eventId = consoleEventId.value;
	if (campaign === null || campaign.proposal == null || eventId === null)
		return null;
	const assets = campaignAssets.value;
	const proposal = campaign.proposal;
	const asset = assets.find((row) => row.id === proposal.assetId) ?? null;
	const now = Date.now();
	const endsAt = campaign.endsAt?.toMillis() ?? now;
	const floor = advertisableFloorCents.get();
	const event = consoleEvent.get();
	return {
		eventId,
		campaign,
		proposal,
		assets,
		asset,
		aiLabel: asset?.provenance === "generatedHere",
		days: Math.max(1, Math.ceil((endsAt - now) / DAY_MS)),
		budgetMinor: campaign.budgetMinor ?? 0,
		spentMinor: campaign.spendMinor ?? 0,
		currency: campaign.currency ?? adAccountCurrency.value ?? null,
		place:
			event === null
				? ""
				: (locationsState.value.find((l) => l.id === event.locationId)?.name ??
					""),
		stale:
			(floor !== null && floor !== campaign.priceFloorCents) ||
			now - (campaign.proposedAt?.toMillis() ?? now) > PROPOSAL_STALE_AFTER_MS,
	};
});
